import type { ErrorInfo } from 'react'

import { createErrorAggregator } from './aggregator'
import { APP_METADATA } from './appMetadata'
import { createDebouncedFlush, createLogFlusher } from './flushLogs'

const EXACT_SENSITIVE_KEYS = new Set([
  // Authentication & Security
  'email',
  'password',
  'creditcard',
  'token',
  'auth',
  'jwt',
  'apitoken',
  'api_token',
  'secret',
  'key',
  'passphrase',
  'credential',

  // Contact Information
  'phone',
  'phonenumber',
  'phone_number',
  'mobile',
  'cell',
  'address',
  'address1',
  'address2',
  'street',
  'city',
  'state',
  'zip',
  'zipcode',
  'firstname',
  'lastname',
  'name',

  // Financial Information
  'creditcard',
  'cvv',
  'cvc',
  'expiry',
  'expirydate',
  'accountnumber',
  'routing',

  // Location Information
  'geolocation',
  'latitude',
  'longitude',
  'coords',
  'coordinates',
  'location',
  'gps',
])

// List of key parts to check for partial matches (more expensive operation)
const PARTIAL_SENSITIVE_KEY_PARTS = [
  'name',
  'user',
  'team',
  'customer',
  'transcription',
]

const SAFE_KEYS = new Set(['appName', 'appVersion', 'userAgent'])

// Combine all regex patterns into a single regex for faster execution
// This is much faster than running multiple regex patterns separately
const COMBINED_PATTERN = new RegExp(
  [
    // Network & Device
    String.raw`\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b`, // IP addresses

    // Personal Identifiers
    String.raw`\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b`, // Emails

    // Phone Numbers
    String.raw`\b(?:\+?\d{1,3}[- ]?)?\(?\d{3}\)?[- ]?\d{3}[- ]?\d{4}\b`, // US/CA Phone
    String.raw`\b(?:\+?\d{1,3}[- ]?)?\d{8,14}\b`, // International Phone (continuous digits)

    // Nordic/EU Phone Numbers (without country codes)
    String.raw`\b\d{2}[ -]?\d{2}[ -]?\d{2}[ -]?\d{2}\b`, // 8-digit with spaces/dashes (DK, NO)
    String.raw`\b0[4-9]\d[ -]?\d{3}[ -]?\d{4}\b`, // Swedish mobile (07X XXX XXXX, 08X XXX XXXX, etc.)
    String.raw`\b\d{2,3}[ -]?\d{3}[ -]?\d{2,4}\b`, // Various EU landline formats
    String.raw`\b\d{8,10}\b`, // 8-10 digit numbers without formatting

    // Financial Information
    String.raw`\b(?:\d{4}[- ]?){3}\d{4}\b`, // Credit card with spaces/dashes
    String.raw`\b\d{16}\b`, // 16-digit credit card

    // API Tokens & Keys
    String.raw`\b[A-Za-z0-9_=-]{8,}(\.[A-Za-z0-9_=-]{8,}){1,2}\b`, // JWT format
  ].join('|'),
  'gi',
)

// Three modes:
// 1. production: always log to service
// 2. dev (default): log to console only
// 3. dev with VITE_ERROR_LOGGER_URL: log to service
const SHOULD_LOG_TO_SERVICE =
  import.meta.env.PROD ||
  (import.meta.env.DEV && !!import.meta.env.VITE_ERROR_LOGGER_URL)

/*
 * Aggregator setup
 */
const aggregator = createErrorAggregator()

/*
 * Flusher setup
 */
const flusher = createLogFlusher(aggregator, {
  shouldLogToService: SHOULD_LOG_TO_SERVICE,
  isOnline: () => navigator.onLine,
})
const flushLogsDebounced = createDebouncedFlush({
  flusher,
  wait: 2000,
  maxWait: 10_000,
})

/**
 * Simple scrubbing logic – you can keep your existing implementation
 */
export type ScrubbedErrorData = {
  message?: string
  stack?: string
  componentStack?: string
  [key: string]: unknown
}

// Types for error data
type ErrorData = {
  message?: string
  stack?: string
  componentStack?: string
  url?: string
  userAgent?: string
  appName?: string
  appVersion?: string
  [key: string]: unknown
}

// Scrub error data
export const scrubError = (
  error: Error,
  errorInfo?: ErrorInfo | null,
): ErrorData => {
  const errorData: ErrorData = {
    message: error.message,
    stack: error.stack,
    componentStack: errorInfo?.componentStack ?? undefined,
    url: window.location.href,
    userAgent: navigator.userAgent,
    ...APP_METADATA,
  }

  return scrubObject(errorData)
}

// Fast check if a key is sensitive
const isSensitiveKey = (key: string): boolean => {
  const lowerKey = key.toLowerCase()

  // Fast path: exact match
  if (EXACT_SENSITIVE_KEYS.has(lowerKey)) {
    return true
  }

  // Slower path: check for partial matches
  return PARTIAL_SENSITIVE_KEY_PARTS.some((part) => lowerKey.includes(part))
}

// Recursively scrub objects, arrays, and strings with circular reference detection.
// `isTopLevel` gates the SAFE_KEYS whitelist: scrubError only ever sets appName/
// appVersion on the top-level errorData, so a nested user object that happens to
// use those keys must still be scrubbed rather than copied verbatim.
export const scrubObject = <T>(
  obj: T,
  visited = new WeakSet(),
  isTopLevel = true,
): T => {
  // Fast path for primitives
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return typeof obj === 'string' ? (scrubString(obj) as unknown as T) : obj
  }

  // Check for circular references
  if (visited.has(obj as object)) {
    return '[CIRCULAR_REFERENCE]' as unknown as T
  }

  // Add current object to visited set
  visited.add(obj as object)

  try {
    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map((item) =>
        scrubObject(item, visited, false),
      ) as unknown as T
    }

    // Handle objects
    const newObj: Record<string, unknown> = {}
    const entries = Object.entries(obj as Record<string, unknown>)

    // Skip unnecessary processing for empty objects
    if (entries.length === 0) {
      return obj
    }

    for (const [key, value] of entries) {
      if (isTopLevel && SAFE_KEYS.has(key)) {
        newObj[key] = value
        continue
      }

      // Use optimized key checking
      if (isSensitiveKey(key)) {
        newObj[key] = '[REDACTED]'
      } else {
        newObj[key] = scrubObject(value, visited, false)
      }
    }

    return newObj as unknown as T
  } finally {
    // Remove from visited set when done (for reusability)
    visited.delete(obj as object)
  }
}

// Optimized string scrubbing using a single regex pattern
export const scrubString = (str: string): string => {
  if (typeof str !== 'string' || str.length === 0) return str

  // Apply the combined regex with a simple replacement
  return str.replace(COMBINED_PATTERN, '[REDACTED]')
}

/**
 * The public function to log an error from React or anywhere else.
 */
export const logError = (error: Error, errorInfo?: ErrorInfo | null) => {
  const scrubbed = scrubError(error, errorInfo)
  aggregator.aggregateError(scrubbed)
  flushLogsDebounced()
}
